/**
 * Memory Store (Zustand)
 *
 * Manages the 8mem knowledge layer state:
 *  - quick-capture inputs
 *  - contextual memory for the currently selected email/task
 *  - search results
 *  - recent captures (last N items stored this session)
 */

import { create } from 'zustand';
import { memoryApi, type MemoryItem, type MemoryContext, type MemoryType, type StoreMemoryPayload } from '../lib/memory-api';

// Types

export interface RecentCapture {
  id: string;           // client-generated UUID for list keys
  content: string;
  type: MemoryType;
  contextSender?: string;
  contextSubject?: string;
  savedAt: string;      // ISO timestamp
}

interface MemoryState {
  // Context feed: auto-populated when an email/task is selected
  activeContext: MemoryContext | null;
  activeContextKey: string | null;   // e.g. "email:sarah@co.com" prevents redundant fetches
  isLoadingContext: boolean;
  contextError: string | null;

  // Search
  searchQuery: string;
  searchResults: MemoryItem[];
  isSearching: boolean;
  searchError: string | null;

  // Recent captures (this session, in-memory only)
  recentCaptures: RecentCapture[];

  // Save state
  isSaving: boolean;
  saveError: string | null;
  lastSavedAt: string | null;

  // Actions
  fetchContext: (params: { sender?: string; subject?: string; taskId?: string }) => Promise<void>;
  clearContext: () => void;
  search: (query: string) => Promise<void>;
  clearSearch: () => void;
  storeMemory: (payload: StoreMemoryPayload) => Promise<void>;
  clearSaveError: () => void;
}

// Store

let contextDebounceTimer: ReturnType<typeof setTimeout> | null = null;

export const useMemoryStore = create<MemoryState>((set, get) => ({
  activeContext: null,
  activeContextKey: null,
  isLoadingContext: false,
  contextError: null,

  searchQuery: '',
  searchResults: [],
  isSearching: false,
  searchError: null,

  recentCaptures: [],

  isSaving: false,
  saveError: null,
  lastSavedAt: null,

  /**
   * Fetch context for the selected email/task.
   * Debounced 300ms to avoid hammering on rapid email navigation.
   * Skips if the context key hasn't changed (e.g. same email re-selected).
   */
  fetchContext: async (params) => {
    const key = [
      params.sender ? `s:${params.sender}` : '',
      params.subject ? `sub:${params.subject}` : '',
      params.taskId ? `t:${params.taskId}` : '',
    ].filter(Boolean).join('|');

    if (!key) {
      set({ activeContext: null, activeContextKey: null });
      return;
    }

    // Skip if same context already loaded
    if (get().activeContextKey === key && get().activeContext !== null) return;

    // Debounce
    if (contextDebounceTimer) clearTimeout(contextDebounceTimer);

    contextDebounceTimer = setTimeout(async () => {
      set({ isLoadingContext: true, contextError: null, activeContextKey: key });

      try {
        const context = await memoryApi.getContext(params);
        // Only apply if the key hasn't changed while we were waiting
        if (get().activeContextKey === key) {
          set({ activeContext: context, isLoadingContext: false });
        }
      } catch (err) {
        if (get().activeContextKey === key) {
          set({
            contextError: err instanceof Error ? err.message : 'Failed to load context',
            isLoadingContext: false,
            activeContext: null,
          });
        }
      }
    }, 300);
  },

  clearContext: () => {
    if (contextDebounceTimer) clearTimeout(contextDebounceTimer);
    set({ activeContext: null, activeContextKey: null, isLoadingContext: false, contextError: null });
  },

  /**
   * Semantic search across 8mem.
   */
  search: async (query: string) => {
    if (!query.trim()) {
      set({ searchQuery: '', searchResults: [], searchError: null });
      return;
    }

    set({ searchQuery: query, isSearching: true, searchError: null });

    try {
      const results = await memoryApi.search(query);
      set({ searchResults: results, isSearching: false });
    } catch (err) {
      set({
        searchError: err instanceof Error ? err.message : 'Search failed',
        isSearching: false,
        searchResults: [],
      });
    }
  },

  clearSearch: () => {
    set({ searchQuery: '', searchResults: [], searchError: null });
  },

  /**
   * Store a quick-capture memory and add it to the local recent captures list.
   */
  storeMemory: async (payload: StoreMemoryPayload) => {
    set({ isSaving: true, saveError: null });

    try {
      await memoryApi.store(payload);

      const capture: RecentCapture = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        content: payload.content,
        type: payload.type ?? 'note',
        contextSender: payload.contextSender,
        contextSubject: payload.contextSubject,
        savedAt: new Date().toISOString(),
      };

      set((state) => ({
        isSaving: false,
        lastSavedAt: capture.savedAt,
        // Prepend and keep only last 20 captures in memory
        recentCaptures: [capture, ...state.recentCaptures].slice(0, 20),
      }));

      // Invalidate context so it refreshes with the new memory
      if (payload.contextSender || payload.contextSubject) {
        set({ activeContext: null, activeContextKey: null });
      }
    } catch (err) {
      set({
        isSaving: false,
        saveError: err instanceof Error ? err.message : 'Failed to save',
      });
      throw err;
    }
  },

  clearSaveError: () => set({ saveError: null }),
}));
